import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "../components/Header";
import ContactFooter from "../components/ContactFooter";
import { SUPPORT_EMAIL, ENTITY_NAME } from "../data/siteContent";

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-parchment min-h-screen antialiased">
      <Header light />
      <article className="pt-36 sm:pt-44 pb-24">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-goldeep text-sm font-medium tracking-wide hover:text-ink transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>

          <h1 className="font-display text-3xl sm:text-4xl text-ink tracking-tight leading-[1.1] mt-8">
            Terms of <em className="text-goldeep italic">Service</em>
          </h1>
          <p className="text-ink/50 text-sm mt-4 tracking-wide">
            Effective date: June 2026 &middot; Iron Gate Consulting, operated by {ENTITY_NAME}
          </p>

          <div className="mt-10 space-y-8 text-ink/75 text-base leading-relaxed">
            <section>
              <h2 className="font-display text-xl text-ink mb-3">1. Acceptance of Terms</h2>
              <p>
                By accessing this website or using the services of Iron Gate
                Consulting, operated by {ENTITY_NAME}, you agree to these
                Terms of Service. If you do not agree, please do not use this
                website or our services.
              </p>
            </section>
            <section>
              <h2 className="font-display text-xl text-ink mb-3">2. Our Services</h2>
              <p>
                Iron Gate Consulting provides living trust setup, asset
                protection structuring, and coordination of legal review
                services for estate planning purposes. Intake is completed
                through our portal at trust.igchq.com. You review everything
                before you pay, and you receive a personal call before
                anything finalizes.
              </p>
              <p className="mt-4">
                Iron Gate Consulting is not a law firm and does not provide
                legal advice, and makes no legal guarantees of any outcome. Legal review
                services are provided by licensed
                attorneys through veteran military benefits and are available
                based on your location. Consult a licensed attorney for legal
                advice specific to your situation. All trust planning services are
                facilitated by licensed professionals; {ENTITY_NAME} coordinates the process.
              </p>
            </section>
            <section>
              <h2 className="font-display text-xl text-ink mb-3">3. Fees and Payment</h2>
              <p>
                Current pricing is displayed on this website: Basic Trust at
                $1,497 and Private Trust at $2,497, with the option to start
                basic and upgrade to private for $1,000 more when you are
                ready. No payment is required until you have completed your
                intake, received your personal call, and confirmed you are
                satisfied. Fees are due before your completed trust is
                released to you.
              </p>
            </section>
            <section>
              <h2 className="font-display text-xl text-ink mb-3">4. Your Responsibilities</h2>
              <p>
                You agree to provide accurate and complete information during
                intake, including details about your assets and
                beneficiaries. The effectiveness of your trust depends on the
                accuracy of the information you provide and on properly
                funding the trust after it is delivered. You are responsible
                for reviewing your documents and asking questions before
                finalizing.
              </p>
            </section>
            <section>
              <h2 className="font-display text-xl text-ink mb-3">5. Free Trust Review</h2>
              <p>
                If you submit an existing trust for review, the review is
                provided at no cost and with no commitment. The review is an
                assessment of structure and completeness and does not
                constitute legal advice.
              </p>
            </section>
            <section>
              <h2 className="font-display text-xl text-ink mb-3">6. Intellectual Property</h2>
              <p>
                All content on this website, including text, design,
                graphics, and branding, is the property of Iron Gate
                Consulting or {ENTITY_NAME} and may not be
                reproduced without written permission. Documents prepared for
                you become yours upon delivery.
              </p>
            </section>
            <section>
              <h2 className="font-display text-xl text-ink mb-3">7. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, Iron Gate Consulting
                and {ENTITY_NAME} shall not be liable for indirect, incidental, or consequential
                damages arising from your use of this website or our
                services. Our total liability for any claim shall not exceed
                the amount you paid for the services giving rise to the
                claim.
              </p>
            </section>
            <section>
              <h2 className="font-display text-xl text-ink mb-3">8. Termination</h2>
              <p>
                You may discontinue the intake process at any time before
                payment with no obligation. We reserve the right to decline
                or discontinue service where we cannot responsibly complete a
                file, in which case any fees paid for undelivered work will
                be refunded.
              </p>
            </section>
            <section>
              <h2 className="font-display text-xl text-ink mb-3">9. Dispute Resolution</h2>
              <p>
                If a disagreement arises out of these Terms or our services, we ask that you
                first contact us at {SUPPORT_EMAIL} so we can attempt to resolve it directly and
                in good faith. If a dispute cannot be resolved informally within a reasonable
                time, it shall be resolved through binding arbitration or in a court of
                competent jurisdiction in the state where {ENTITY_NAME} is organized, on an
                individual basis rather than as part of any class or representative action,
                except where prohibited by applicable law.
              </p>
            </section>
            <section>
              <h2 className="font-display text-xl text-ink mb-3">10. Changes to These Terms</h2>
              <p>
                We may update these Terms of Service from time to time.
                Changes will be posted on this page with an updated effective
                date. Continued use of the website or services after changes
                are posted constitutes acceptance of the revised terms.
              </p>
            </section>
            <section>
              <h2 className="font-display text-xl text-ink mb-3">11. Contact</h2>
              <p>
                Questions about these Terms of Service can be sent to{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-goldeep underline">
                  {SUPPORT_EMAIL}
                </a>{" "}
                or through our{" "}
                <Link to="/contact" className="text-goldeep underline">
                  Contact page
                </Link>
                .
              </p>
            </section>
            <p className="text-ink/50 text-sm italic pt-6 border-t border-ink/10">
              Iron Gate Consulting is not a law firm and is operated by {ENTITY_NAME}. Legal
              review services are provided through veteran military benefits. Trust planning
              services are facilitated by licensed professionals and come with
              no legal guarantees. Consult a licensed
              attorney for legal advice.
            </p>
          </div>
        </div>
      </article>
      <ContactFooter />
    </div>
  );
};

export default TermsOfService;
